import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, User, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification sound as base64 (pleasant chime sound)
const NOTIFICATION_SOUND = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNkTsZAAAAAAD/+9DEAAAIAANIAAAAEygAa0AAAASHJJJJJf/E8k//8jTNIYlkkl//ylKUxrGsaxrJJJEREYxhBgYxhBiIiIwMYGBjCDAxjCDEREYxhBgYxhBiIiIwMYGBjCDAxjCDAAAAAAAAAAA//vQxBcAAADSAAAAAAAAANIAAAAAREREYxhBgYxhBiIiIwMYGBjCDAxjCDEREYxhBgYxhBiIiIwMYGBjCDAxjCDEREYxhBgYxhBiIiIwMYGBjCDAxjCDAAAAAAAAAAAA';

// Play notification sound
export const playNotificationSound = (volume = 0.5) => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND);
    audio.volume = volume;
    audio.play().catch(() => { /* Browser may block autoplay */ });
  } catch (e) {
    // Audio not supported
  }
};

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

// Show browser notification
export const showBrowserNotification = (title, body, onClick) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/Orange.png',
      badge: '/Orange.png',
      tag: 'mylocaltrade-message',
      renotify: true
    });
    
    notification.onclick = () => {
      window.focus();
      onClick?.();
      notification.close();
    };
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  }
};

// Message Notification Popup Component
export function MessageNotificationPopup({ notification, onClose, onView }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Auto hide after 8 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 8000);
    
    return () => clearTimeout(timer);
  }, [onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed top-4 right-4 z-[100] bg-white rounded-xl border-2 border-mlt-orange/20 p-4 w-80 transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
      data-testid="message-notification-popup"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-mlt-orange rounded-full flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold text-mlt-navy truncate">{notification.senderName}</p>
            <button 
              onClick={onClose}
              className="text-mlt-text-muted hover:text-mlt-text-secondary"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-mlt-text-secondary line-clamp-2 mb-3">
            {notification.message}
          </p>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={onView}
              className="bg-mlt-orange-hover hover:bg-mlt-orange-hover text-xs"
            >
              View Message
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={onClose}
              className="text-xs"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </div>
      
      {/* Animated progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-mlt-off-white rounded-b-2xl overflow-hidden">
        <div 
          className="h-full bg-mlt-orange animate-shrink"
          style={{ animation: 'shrink 8s linear forwards' }}
        />
      </div>
      
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

// Global Notification Manager Component
export default function NotificationManager() {
  const { user } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const lastCheckedRef = useRef(Date.now());

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Poll for new notifications
  useEffect(() => {
    if (!user) return;

    const checkNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications?unread_only=true`, {
          withCredentials: true
        });
        
        const newNotifications = response.data.notifications.filter(
          n => new Date(n.created_at).getTime() > lastCheckedRef.current && n.type === 'new_message'
        );
        
        if (newNotifications.length > 0) {
          // Play sound
          playNotificationSound(0.6);
          
          // Show popup notifications
          newNotifications.forEach(notif => {
            // Add to state for in-app popup
            setNotifications(prev => [...prev, {
              id: notif.id,
              senderName: notif.sender_name || 'Someone',
              message: notif.message,
              conversationId: notif.conversation_id
            }]);
            
            // Show browser notification if page is not focused
            if (document.hidden) {
              showBrowserNotification(
                `New message from ${notif.sender_name || 'Someone'}`,
                notif.message,
                () => {
                  // Focus window and open chat
                  window.focus();
                }
              );
            }
          });
          
          lastCheckedRef.current = Date.now();
        }
      } catch (error) {
        // Ignore errors
      }
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 3000);
    
    return () => clearInterval(interval);
  }, [user]);

  const handleDismiss = useCallback((notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  }, []);

  const handleView = useCallback((notification) => {
    handleDismiss(notification.id);
    window.location.href = '/account?tab=chat';
  }, [handleDismiss]);

  return (
    <>
      {notifications.map((notif, index) => (
        <div 
          key={notif.id} 
          style={{ top: `${16 + (index * 140)}px` }}
          className="fixed right-4 z-[100]"
        >
          <MessageNotificationPopup
            notification={notif}
            onClose={() => handleDismiss(notif.id)}
            onView={() => handleView(notif)}
          />
        </div>
      ))}
    </>
  );
}
